import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { language } from "../../../languageProvider/language/language";
import { defaultLanguage } from "../../../helper/utility";

const L = (key, fallback) =>
  (language[defaultLanguage] && language[defaultLanguage][key]) || fallback;

const MAX_VISIBLE_RESULTS = 30;
const DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

/**
 * Mobile bottom-sheet that hosts a live product search experience.
 *
 * Phases:
 *  - idle:      no query yet  → suggested category chips
 *  - too-short: query.length === 1 → hint under input, idle body
 *  - loading:   query.length >= 2 (debouncing) → 3 row skeleton
 *  - results:   matches found → list (max 30 rows) + footer CTA if needed
 *  - empty:     no matches    → illustration + clear CTA
 *
 * The sheet is always mounted (open/closed via class+transform) so the user
 * pending query is preserved between openings.
 */
const SearchBottomSheet = ({
  open,
  onClose,
  onSeeAllResults,
  onProductSelect,
  products = [],
  categories = [],
  onCategorySelect,
  currentPathname = "",
  initialQuery = "",
}) => {
  const [query, setQuery] = useState(initialQuery || "");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [phase, setPhase] = useState("idle");
  const [matches, setMatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const inputRef = useRef(null);
  const sheetRef = useRef(null);
  const prevOpenRef = useRef(false);
  const debounceRef = useRef(null);
  const dragState = useRef({ startY: 0, startTime: 0, lastY: 0 });

  // ---- Search core ----------------------------------------------------------
  const runSearch = useCallback(
    (q) => {
      const needle = String(q || "").toLowerCase();
      const filtered = (products || []).filter((p) =>
        String(p && p.description ? p.description : "")
          .toLowerCase()
          .includes(needle)
      );
      const total = filtered.length;
      setTotalCount(total);
      setMatches(filtered.slice(0, MAX_VISIBLE_RESULTS));
      setDebouncedQuery(q);
      setPhase(total > 0 ? "results" : "empty");
    },
    [products]
  );

  // ---- Query lifecycle ------------------------------------------------------
  // Recompute phase whenever the query changes; debounce the actual search.
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const trimmed = (query || "").trim();
    if (trimmed.length === 0) {
      setPhase("idle");
      setMatches([]);
      setTotalCount(0);
      setDebouncedQuery("");
      return undefined;
    }
    if (trimmed.length < MIN_QUERY_LEN) {
      setPhase("too-short");
      setMatches([]);
      setTotalCount(0);
      setDebouncedQuery("");
      return undefined;
    }
    setPhase("loading");
    debounceRef.current = window.setTimeout(() => {
      runSearch(trimmed);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [query, runSearch]);

  // Sync initialQuery into local state on a closed -> open transition only.
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Reset drag state; preserve the query unless caller supplied a new one.
      setDragY(0);
      setIsDragging(false);
      if (typeof initialQuery === "string" && initialQuery !== query) {
        setQuery(initialQuery);
      }
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  // ---- Body lock ------------------------------------------------------------
  useLayoutEffect(() => {
    if (open) {
      document.body.classList.add("pf-sheet-open");
    } else {
      document.body.classList.remove("pf-sheet-open");
    }
    return () => {
      document.body.classList.remove("pf-sheet-open");
    };
  }, [open]);

  // ---- Auto-focus input after open (AC8) ------------------------------------
  useEffect(() => {
    if (open && inputRef.current) {
      const id = window.setTimeout(() => {
        const el = inputRef.current;
        if (!el) return;
        try {
          el.focus({ preventScroll: true });
        } catch (_e) {
          el.focus();
        }
        try {
          const len = el.value ? el.value.length : 0;
          if (len > 0) el.setSelectionRange(0, len);
        } catch (_e) {
          /* iOS may throw on certain input types; safe to ignore */
        }
      }, 250);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // ---- ESC to close ---------------------------------------------------------
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose && onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // ---- Drag-to-dismiss (header only) ---------------------------------------
  const onTouchStart = useCallback((e) => {
    if (!e.touches || e.touches.length === 0) return;
    const y = e.touches[0].clientY;
    dragState.current.startY = y;
    dragState.current.lastY = y;
    dragState.current.startTime = Date.now();
    setIsDragging(true);
  }, []);

  const onTouchMove = useCallback(
    (e) => {
      if (!isDragging || !e.touches || e.touches.length === 0) return;
      const y = e.touches[0].clientY;
      dragState.current.lastY = y;
      const dy = Math.max(0, y - dragState.current.startY);
      setDragY(dy);
    },
    [isDragging]
  );

  const onTouchEnd = useCallback(() => {
    if (!isDragging) return;
    const elapsed = Math.max(1, Date.now() - dragState.current.startTime);
    const dy = Math.max(0, dragState.current.lastY - dragState.current.startY);
    const velocity = dy / elapsed;
    setIsDragging(false);
    if (dy > 120 || velocity > 0.6) {
      setDragY(0);
      onClose && onClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, onClose]);

  // ---- Handlers -------------------------------------------------------------
  const handleClearQuery = useCallback(() => {
    setQuery("");
    if (inputRef.current) {
      try {
        inputRef.current.focus({ preventScroll: true });
      } catch (_e) {
        inputRef.current.focus();
      }
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const handleProductTap = useCallback(
    (productId) => {
      if (typeof onProductSelect === "function") onProductSelect(productId);
      onClose && onClose();
    },
    [onClose, onProductSelect]
  );

  const handleSeeAll = useCallback(() => {
    const q = (debouncedQuery || query || "").trim();
    if (typeof onSeeAllResults === "function") onSeeAllResults(q);
    onClose && onClose();
  }, [debouncedQuery, query, onClose, onSeeAllResults]);

  const handleCategoryChip = useCallback(
    (name) => {
      if (typeof onCategorySelect === "function") onCategorySelect(name);
      onClose && onClose();
    },
    [onCategorySelect, onClose]
  );

  // ---- Derived data --------------------------------------------------------
  const suggestCategories = useMemo(
    () => (Array.isArray(categories) ? categories.slice(0, 6) : []),
    [categories]
  );

  const showFooterCta = useMemo(() => {
    if (phase !== "results") return false;
    if (totalCount > MAX_VISIBLE_RESULTS) return true;
    if (totalCount >= MIN_QUERY_LEN && currentPathname !== "/categories")
      return true;
    return false;
  }, [phase, totalCount, currentPathname]);

  const resultsHint = useMemo(() => {
    if (phase !== "results") return "";
    if (totalCount > MAX_VISIBLE_RESULTS) {
      return L("n_risultati_primi_30", "{n} results · top 30").replace(
        "{n}",
        String(totalCount)
      );
    }
    return L("n_risultati_trovati", "{n} results found").replace(
      "{n}",
      String(totalCount)
    );
  }, [phase, totalCount]);

  // ---- Labels --------------------------------------------------------------
  const titleLabel = L("cerca", "Search");
  const placeholderLabel = L("cerca_un_prodotto", "Search a product…");
  const closeLabel = L("chiudi_ricerca", "Close search");
  const clearLabel = L("pulisci_ricerca", "Clear search");
  const tooShortHint = L("digita_almeno_2", "Type at least 2 characters");
  const idleTitle = L(
    "inizia_a_digitare",
    "Start typing to search a product in the active flyer"
  );
  const idleSuggestLabel = L("categorie_suggerite", "Suggested categories");
  const emptyTitleTpl = L("nessun_risultato_per", "No results for «{q}»");
  const emptySub = L(
    "prova_altro_termine",
    "Try a different term or check spelling"
  );
  const emptyShort = L("nessun_risultato_short", "No results");
  const seeAllTpl = L(
    "vedi_tutti_n_risultati",
    "See all {n} results in the list"
  );

  // ---- Sheet styling -------------------------------------------------------
  const sheetStyle = open
    ? {
        transform: `translateY(${dragY}px)`,
        transition: isDragging
          ? "none"
          : "transform .28s cubic-bezier(.32, .72, 0, 1)",
      }
    : undefined;

  const backdropOpacity = open ? Math.max(0, 1 - dragY / 200) : 0;

  // ---- Render helpers ------------------------------------------------------
  const renderSearchIcon = () => (
    <svg
      className="pf-search-sheet-icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  );

  const renderClearIcon = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  const renderChevron = () => (
    <svg
      className="pf-search-result-chevron"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );

  const renderIllustrationSearch = () => (
    <div className="pf-search-state-illus" aria-hidden="true">
      <svg
        width="44"
        height="44"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="20" y1="20" x2="16.65" y2="16.65" />
      </svg>
    </div>
  );

  const renderIllustrationEmpty = () => (
    <div className="pf-search-state-illus" aria-hidden="true">
      <svg
        width="44"
        height="44"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
      </svg>
    </div>
  );

  const getProductImage = (p) => {
    if (!p) return null;
    if (Array.isArray(p.images) && p.images.length > 0) {
      // Prefer first non-cropped image like Product.jsx does.
      const nonCropped = p.images.find((img) => img && img.cropped === false);
      const pick = nonCropped || p.images[0];
      if (pick && pick.image_file) return pick.image_file;
    }
    if (p.img && typeof p.img === "object" && p.img.src) return p.img.src;
    if (typeof p.thumb === "string") return p.thumb;
    return null;
  };

  const getProductName = (p) => {
    if (!p) return "";
    if (p.description && String(p.description).trim()) {
      return String(p.description).trim();
    }
    // Fallback compose from brand + field1
    const parts = [p.field2, p.field1, p.field3].filter(
      (s) => s && String(s).trim()
    );
    return parts.join(" ").trim();
  };

  const getProductPrice = (p) => {
    if (!p) return "";
    if (typeof p.price_label === "string" && p.price_label.trim())
      return p.price_label.trim();
    if (typeof p.price === "string" && p.price.trim()) return p.price.trim();
    if (typeof p.price === "number") return String(p.price);
    return "";
  };

  const renderRow = (p, idx) => {
    const productId = p.item_id || p.id || idx;
    const img = getProductImage(p);
    const name = getProductName(p);
    const price = getProductPrice(p);
    return (
      <button
        key={`pfsr_${productId}_${idx}`}
        type="button"
        className="pf-search-result-row"
        onClick={() => handleProductTap(productId)}
      >
        <div className="pf-search-result-thumb">
          {img ? (
            <img
              src={img}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
        <div className="pf-search-result-content">
          <span className="pf-search-result-name">{name || "—"}</span>
          {price ? (
            <span className="pf-search-result-price">{price}</span>
          ) : null}
        </div>
        {renderChevron()}
      </button>
    );
  };

  const renderSkeletonRow = (key) => (
    <div className="pf-search-skeleton-row" key={`pfsk_${key}`} aria-hidden="true">
      <div className="pf-search-skeleton-thumb" />
      <div className="pf-search-skeleton-content">
        <div
          className="pf-search-skeleton-line"
          style={{ width: "70%" }}
        />
        <div
          className="pf-search-skeleton-line"
          style={{ width: "40%" }}
        />
      </div>
    </div>
  );

  const renderIdle = () => (
    <div className="pf-search-state">
      {renderIllustrationSearch()}
      <h3 className="pf-search-state-title">{titleLabel}</h3>
      <p className="pf-search-state-sub">{idleTitle}</p>
      {suggestCategories.length > 0 && (
        <>
          <div
            className="pf-search-state-sub pf-search-suggest-label"
            style={{ marginTop: 8 }}
          >
            {idleSuggestLabel}
          </div>
          <div className="pf-search-suggest">
            {suggestCategories.map((c) => (
              <button
                key={`pfssc_${c.id || c.name}`}
                type="button"
                className="pf-chip-filter"
                onClick={() => handleCategoryChip(c.name)}
                title={c.name || ""}
              >
                {c.logo ? (
                  <img
                    className="pf-chip-filter__img"
                    src={c.logo}
                    alt=""
                    aria-hidden="true"
                  />
                ) : null}
                <span className="pf-chip-filter__label">{c.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderLoading = () => (
    <div className="pf-search-results-list" role="status" aria-live="polite">
      {renderSkeletonRow(0)}
      {renderSkeletonRow(1)}
      {renderSkeletonRow(2)}
    </div>
  );

  const renderResults = () => (
    <div className="pf-search-results-list">
      {matches.map((p, idx) => renderRow(p, idx))}
    </div>
  );

  const renderEmpty = () => (
    <div className="pf-search-state">
      {renderIllustrationEmpty()}
      <h3 className="pf-search-state-title">
        {emptyTitleTpl.replace("{q}", debouncedQuery || query || "")}
      </h3>
      <p className="pf-search-state-sub">{emptySub}</p>
      <button
        type="button"
        className="pf-search-state-action"
        onClick={handleClearQuery}
      >
        {clearLabel}
      </button>
    </div>
  );

  // ---- Body switch by phase -----------------------------------------------
  const renderBody = () => {
    if (phase === "results") return renderResults();
    if (phase === "loading") return renderLoading();
    if (phase === "empty") return renderEmpty();
    // idle + too-short share the same body
    return renderIdle();
  };

  // Above-input hint (visible for too-short / results / empty)
  const headerHint = (() => {
    if (phase === "too-short") return tooShortHint;
    if (phase === "results") return resultsHint;
    if (phase === "empty") return emptyShort;
    return "";
  })();

  // ---- Markup --------------------------------------------------------------
  const node = (
    <div
      className={`pf-sheet-root ${open ? "pf-sheet-root--open" : ""}`}
      aria-hidden={!open}
    >
      <div
        className={`pf-sheet-backdrop ${
          open ? "pf-sheet-backdrop--open" : ""
        }`}
        style={open ? { opacity: backdropOpacity } : undefined}
        onClick={() => onClose && onClose()}
      />

      <div
        ref={sheetRef}
        className={`pf-sheet ${open ? "pf-sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-search-sheet-title"
        style={sheetStyle}
      >
        <div
          className="pf-sheet__grabber-zone"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <span className="pf-sheet__grabber" aria-hidden="true" />
        </div>

        <div
          className="pf-sheet__header"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <div className="pf-sheet__header-text">
            <h2 id="pf-search-sheet-title" className="pf-sheet__title">
              {titleLabel}
            </h2>
          </div>
          <button
            type="button"
            className="pf-sheet__close"
            aria-label={closeLabel}
            onClick={() => onClose && onClose()}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="pf-search-sheet-input-wrap">
          {renderSearchIcon()}
          <input
            ref={inputRef}
            type="search"
            className="pf-search-sheet-input"
            placeholder={placeholderLabel}
            value={query}
            onChange={handleInputChange}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            enterKeyHint="search"
            aria-label={placeholderLabel}
          />
          {query.length > 0 && (
            <button
              type="button"
              className="pf-search-sheet-clear"
              onClick={handleClearQuery}
              aria-label={clearLabel}
            >
              {renderClearIcon()}
            </button>
          )}
        </div>

        {headerHint ? (
          <p className="pf-search-sheet-hint" role="status" aria-live="polite">
            {headerHint}
          </p>
        ) : null}

        <div className="pf-sheet__body pf-search-sheet-body">
          {renderBody()}
        </div>

        {showFooterCta && (
          <div className="pf-search-footer-cta">
            <button
              type="button"
              className="pf-search-footer-cta__btn"
              onClick={handleSeeAll}
            >
              {seeAllTpl.replace("{n}", String(totalCount))}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(node, document.body);
};

export default SearchBottomSheet;
